// AI Drive design-system React components (typed port of the Claude Design
// project primitives). Pure markup + tokens — icons are passed in as children.
import {
  useEffect,
  useState,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";

/* ----------------------------- Button ----------------------------- */
type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ControlSize = "sm" | "md" | "lg";

export function Button({
  children,
  variant = "secondary",
  size = "md",
  icon = null,
  iconRight = null,
  disabled = false,
  loading = false,
  block = false,
  type = "button",
  onClick,
  style = {},
  ...rest
}: {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: ControlSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  block?: boolean;
  type?: "button" | "submit" | "reset";
  onClick?: () => void;
  style?: CSSProperties;
}) {
  const heights = { sm: "var(--control-h-sm)", md: "var(--control-h-md)", lg: "var(--control-h-lg)" };
  const pads = { sm: "0 10px", md: "0 14px", lg: "0 20px" };
  const fonts = { sm: "var(--text-xs)", md: "var(--text-sm)", lg: "var(--text-base)" };
  const variants: Record<ButtonVariant, CSSProperties> = {
    primary: { background: "var(--accent)", color: "var(--text-on-accent)", borderColor: "var(--accent)", fontWeight: 700 },
    secondary: { background: "var(--surface-raised)", color: "var(--text-body)", borderColor: "var(--border-strong)", fontWeight: 500 },
    ghost: { background: "transparent", color: "var(--text-dim)", borderColor: "transparent", fontWeight: 500 },
    danger: { background: "transparent", color: "var(--status-error)", borderColor: "var(--status-error)", fontWeight: 500 },
  };
  const v = variants[variant];
  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      style={{
        display: block ? "flex" : "inline-flex",
        width: block ? "100%" : "auto",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.6ch",
        height: heights[size],
        minHeight: heights[size],
        padding: pads[size],
        fontFamily: "var(--font-mono)",
        fontSize: fonts[size],
        fontWeight: v.fontWeight,
        letterSpacing: "0.01em",
        lineHeight: 1,
        color: v.color,
        background: v.background,
        border: "1px solid",
        borderColor: v.borderColor,
        borderRadius: "var(--radius-md)",
        cursor: disabled || loading ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        whiteSpace: "nowrap",
        transition: "background var(--dur-fast) var(--ease-out), border-color var(--dur-fast) var(--ease-out)",
        ...style,
      }}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
      {iconRight}
    </button>
  );
}

function Spinner() {
  const frames = ["⠋", "⠙", "⠹", "⠸", "⠼", "⠴", "⠦", "⠧", "⠇", "⠏"];
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % frames.length), 80);
    return () => clearInterval(t);
  }, []);
  return <span style={{ fontFamily: "var(--font-mono)" }}>{frames[i]}</span>;
}

/* ----------------------------- IconButton ----------------------------- */
export function IconButton({
  children,
  label,
  size = "md",
  onClick,
  style = {},
}: {
  children: ReactNode;
  label: string;
  size?: "sm" | "md";
  onClick?: () => void;
  style?: CSSProperties;
}) {
  const dim = size === "sm" ? 28 : 34;
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: dim,
        height: dim,
        minHeight: dim,
        padding: 0,
        color: "var(--text-muted)",
        background: "transparent",
        border: "1px solid var(--border-strong)",
        borderRadius: "var(--radius-md)",
        cursor: "pointer",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

/* ----------------------------- Panel ----------------------------- */
const dot: CSSProperties = { width: 9, height: 9, borderRadius: 999, background: "var(--ink-600)", display: "block" };

export function Panel({
  title,
  path,
  dots = true,
  actions = null,
  footer = null,
  scanlines = false,
  glow = false,
  children,
  style = {},
  bodyStyle = {},
}: {
  title?: ReactNode;
  path?: ReactNode;
  dots?: boolean;
  actions?: ReactNode;
  footer?: ReactNode;
  scanlines?: boolean;
  glow?: boolean;
  children?: ReactNode;
  style?: CSSProperties;
  bodyStyle?: CSSProperties;
}) {
  return (
    <section
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        background: "var(--surface-panel)",
        border: "1px solid var(--border-strong)",
        borderRadius: "var(--radius-lg)",
        boxShadow: glow ? "var(--glow-soft-amber), var(--shadow-md)" : "var(--shadow-md)",
        overflow: "hidden",
        ...style,
      }}
    >
      {(title || path || dots || actions) && (
        <header
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "0 12px",
            height: "var(--control-h-md)",
            flexShrink: 0,
            background: "var(--surface-sunken)",
            borderBottom: "1px solid var(--border-strong)",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--text-xs)",
          }}
        >
          {dots && (
            <span style={{ display: "inline-flex", gap: 6 }}>
              <i style={dot} />
              <i style={dot} />
              <i style={dot} />
            </span>
          )}
          {(title || path) && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.6ch", color: "var(--text-muted)", minWidth: 0 }}>
              {title && <span style={{ color: "var(--text-dim)", fontWeight: 600 }}>{title}</span>}
              {path && <span style={{ color: "var(--text-faint)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{path}</span>}
            </span>
          )}
          {actions && <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6 }}>{actions}</span>}
        </header>
      )}
      <div style={{ position: "relative", flex: 1, minHeight: 0, padding: "16px", ...bodyStyle }}>
        {scanlines && <span className="ad-scanlines" />}
        {children}
      </div>
      {footer && (
        <footer
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "0 12px",
            height: "26px",
            flexShrink: 0,
            background: "var(--surface-sunken)",
            borderTop: "1px solid var(--border-strong)",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--text-2xs)",
            color: "var(--text-faint)",
          }}
        >
          {footer}
        </footer>
      )}
    </section>
  );
}

/* ----------------------------- Badge ----------------------------- */
type BadgeTone = "accent" | "neutral" | "online" | "error" | "agent";
const BADGE_TONES: Record<BadgeTone, { fg: string; bg: string; bd: string }> = {
  accent: { fg: "var(--accent)", bg: "var(--amber-glow)", bd: "rgba(232,176,70,0.3)" },
  neutral: { fg: "var(--text-muted)", bg: "var(--surface-raised)", bd: "var(--border-strong)" },
  online: { fg: "var(--status-online)", bg: "var(--green-glow)", bd: "rgba(77,191,122,0.3)" },
  error: { fg: "var(--status-error)", bg: "var(--red-glow)", bd: "rgba(229,96,77,0.3)" },
  agent: { fg: "var(--status-agent)", bg: "rgba(207,123,224,0.12)", bd: "rgba(207,123,224,0.3)" },
};

export function Badge({
  children,
  tone = "neutral",
  dot: showDot = false,
  style = {},
}: {
  children: ReactNode;
  tone?: BadgeTone;
  dot?: boolean;
  style?: CSSProperties;
}) {
  const t = BADGE_TONES[tone];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 8px",
        fontFamily: "var(--font-mono)",
        fontSize: "var(--text-2xs)",
        fontWeight: 600,
        color: t.fg,
        background: t.bg,
        border: `1px solid ${t.bd}`,
        borderRadius: "var(--radius-pill)",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {showDot && <span style={{ width: 6, height: 6, borderRadius: 999, background: t.fg, display: "block" }} />}
      {children}
    </span>
  );
}

/* ----------------------------- Tag ----------------------------- */
export function Tag({ children, color, style = {} }: { children: ReactNode; color?: string; style?: CSSProperties }) {
  const c = color || "var(--text-faint)";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "1px 7px",
        fontFamily: "var(--font-mono)",
        fontSize: "var(--text-2xs)",
        color: c,
        background: "var(--surface-raised)",
        border: "1px solid var(--border-strong)",
        borderRadius: "var(--radius-sm)",
        ...style,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 2, background: c, display: "block" }} />
      {children}
    </span>
  );
}

/* ----------------------------- StatusDot ----------------------------- */
type DotTone = "online" | "agent" | "idle" | "error" | "accent" | "warn";
const DOT_TONES: Record<DotTone, string> = {
  online: "var(--status-online)",
  agent: "var(--status-agent)",
  idle: "var(--text-disabled)",
  error: "var(--status-error)",
  accent: "var(--accent)",
  warn: "var(--status-warn)",
};

export function StatusDot({
  tone = "online",
  pulse = false,
  size = 8,
  label,
}: {
  tone?: DotTone;
  pulse?: boolean;
  size?: number;
  label?: ReactNode;
}) {
  const color = DOT_TONES[tone];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontFamily: "var(--font-mono)", fontSize: "var(--text-2xs)", color: "var(--text-faint)" }}>
      <span
        style={{
          width: size,
          height: size,
          borderRadius: 999,
          background: color,
          display: "block",
          boxShadow: `0 0 0 3px ${color}22`,
          animation: pulse ? "ds-pulse 1.6s ease-in-out infinite" : undefined,
        }}
      />
      {label}
    </span>
  );
}

/* ----------------------------- Input ----------------------------- */
export function Input({
  prefix,
  style = {},
  ...rest
}: { prefix?: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        height: "var(--control-h-md)",
        padding: "0 12px",
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-strong)",
        borderRadius: "var(--radius-md)",
        ...style,
      }}
    >
      {prefix && <span style={{ color: "var(--accent)", fontFamily: "var(--font-mono)", fontWeight: 700 }}>{prefix}</span>}
      <input
        {...rest}
        style={{
          flex: 1,
          minWidth: 0,
          height: "100%",
          background: "transparent",
          border: "none",
          outline: "none",
          color: "var(--text-body)",
          fontFamily: "var(--font-mono)",
          fontSize: "var(--text-sm)",
        }}
      />
    </span>
  );
}

/* ----------------------------- Tabs ----------------------------- */
export type TabItem = { id: string; label: ReactNode; icon?: ReactNode; count?: number | null };

export function Tabs({
  items,
  value,
  onChange,
  style = {},
}: {
  items: TabItem[];
  value: string;
  onChange?: (id: string) => void;
  style?: CSSProperties;
}) {
  return (
    <div role="tablist" style={{ display: "flex", gap: 2, borderBottom: "1px solid var(--border-strong)", fontFamily: "var(--font-mono)", ...style }}>
      {items.map((it) => {
        const on = it.id === value;
        return (
          <button
            key={it.id}
            role="tab"
            aria-selected={on}
            onClick={() => onChange?.(it.id)}
            style={{
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.7ch",
              padding: "8px 14px",
              marginBottom: -1,
              minHeight: 0,
              fontFamily: "inherit",
              fontSize: "var(--text-sm)",
              fontWeight: on ? 600 : 500,
              color: on ? "var(--text-strong)" : "var(--text-muted)",
              background: "none",
              border: "none",
              borderBottom: `2px solid ${on ? "var(--accent)" : "transparent"}`,
              cursor: "pointer",
            }}
          >
            {it.icon}
            {it.label}
            {it.count != null && (
              <span style={{ fontSize: "var(--text-2xs)", color: "var(--text-faint)", background: "var(--surface-raised)", borderRadius: "var(--radius-sm)", padding: "0 5px" }}>{it.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ----------------------------- Kbd ----------------------------- */
export function Kbd({ children, style = {} }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <kbd
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "1.7em",
        height: "1.7em",
        padding: "0 0.5em",
        fontFamily: "var(--font-mono)",
        fontSize: "var(--text-2xs)",
        fontWeight: 600,
        color: "var(--text-dim)",
        background: "var(--surface-raised)",
        border: "1px solid var(--border-strong)",
        borderBottomWidth: "2px",
        borderRadius: "var(--radius-sm)",
        lineHeight: 1,
        ...style,
      }}
    >
      {children}
    </kbd>
  );
}

/* ----------------------------- ProgressBar ----------------------------- */
type ProgressTone = "accent" | "online" | "info" | "error";
export function ProgressBar({
  value = 0,
  max = 100,
  tone = "accent",
  label,
  style = {},
}: {
  value?: number;
  max?: number;
  tone?: ProgressTone;
  label?: ReactNode;
  style?: CSSProperties;
}) {
  const pct = Math.max(0, Math.min(1, max ? value / max : 0));
  const colors: Record<ProgressTone, string> = {
    accent: "var(--accent)",
    online: "var(--status-online)",
    info: "var(--status-info)",
    error: "var(--status-error)",
  };
  const c = colors[tone];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, ...style }}>
      {label != null && (
        <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "var(--font-mono)", fontSize: "var(--text-2xs)", color: "var(--text-faint)" }}>
          <span>{label}</span>
          <span>{Math.round(pct * 100)}%</span>
        </div>
      )}
      <div style={{ height: 6, background: "var(--ink-700)", borderRadius: "var(--radius-pill)", overflow: "hidden" }}>
        <div style={{ width: `${pct * 100}%`, height: "100%", background: c, boxShadow: `0 0 8px -1px ${c}`, borderRadius: "inherit", transition: "width var(--dur-slow) var(--ease-out)" }} />
      </div>
    </div>
  );
}
